package com.bulut.mecitogullari.ui.screens.purchase

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.parseDecimalAmount
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.AccountKind
import com.bulut.mecitogullari.data.model.DebtEntry
import com.bulut.mecitogullari.data.model.DebtEntryType
import com.bulut.mecitogullari.data.model.DebtItem
import com.bulut.mecitogullari.data.repository.DebtRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.math.roundToLong

/** One editable purchase line. Quantity and price accept comma decimals. */
data class ItemDraft(
    val name: String = "",
    val quantityText: String = "",
    val unit: String = "Adet",
    val unitPriceText: String = "",
) {
    val quantity: Double? get() = parseDecimalAmount(quantityText)
    val unitPrice: Double? get() = parseDecimalAmount(unitPriceText)
    val total: Double get() = (quantity ?: 0.0) * (unitPrice ?: 0.0)
    val isValid: Boolean
        get() = name.isNotBlank() && (quantity ?: 0.0) > 0 && (unitPrice ?: 0.0) > 0
    val isBlank: Boolean
        get() = name.isBlank() && quantityText.isBlank() && unitPriceText.isBlank()
}

data class PurchaseUiState(
    val isEditing: Boolean = false,
    /** true when this account is a customer → "Malzeme Satımı". */
    val isCustomer: Boolean = false,
    val dateEpochMillis: Long = todayUtcMidnightMillis(),
    val receiver: String = "",
    val items: List<ItemDraft> = listOf(ItemDraft()),
    val error: String? = null,
    val isSaving: Boolean = false,
    val closed: Boolean = false,
) {
    val total: Double get() = items.sumOf { it.total }
}

/**
 * Malzeme alımı: teslim alan + kalem listesi.
 * [entryId] is "" for a new purchase, otherwise the entry being edited.
 */
class PurchaseViewModel(
    private val supplierId: String,
    entryId: String,
    private val debtRepository: DebtRepository,
) : ViewModel() {

    private val editingId: String? = entryId.ifBlank { null }

    private val _uiState = MutableStateFlow(PurchaseUiState(isEditing = editingId != null))
    val uiState: StateFlow<PurchaseUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            debtRepository.suppliers.collect { suppliers ->
                val kind = suppliers.firstOrNull { it.id == supplierId }?.kind
                _uiState.update { it.copy(isCustomer = kind == AccountKind.CUSTOMER) }
            }
        }
        editingId?.let { id ->
            viewModelScope.launch {
                debtRepository.getEntry(id)?.let { entry ->
                    _uiState.update {
                        it.copy(
                            dateEpochMillis = entry.dateEpochMillis,
                            receiver = entry.description,
                            items = entry.items.map { item ->
                                ItemDraft(
                                    name = item.name,
                                    quantityText = formatDecimalForInput(item.quantity),
                                    unit = item.unit,
                                    unitPriceText = formatDecimalForInput(item.unitPrice),
                                )
                            }.ifEmpty { listOf(ItemDraft()) },
                        )
                    }
                }
            }
        }
    }

    fun onDateChange(epochMillis: Long) = _uiState.update { it.copy(dateEpochMillis = epochMillis) }

    fun onReceiverChange(value: String) = _uiState.update { it.copy(receiver = value, error = null) }

    fun onItemChange(index: Int, item: ItemDraft) = _uiState.update { state ->
        state.copy(
            items = state.items.mapIndexed { i, existing -> if (i == index) item else existing },
            error = null,
        )
    }

    fun addItem() = _uiState.update { it.copy(items = it.items + ItemDraft()) }

    fun removeItem(index: Int) = _uiState.update { state ->
        val remaining = state.items.filterIndexed { i, _ -> i != index }
        state.copy(items = remaining.ifEmpty { listOf(ItemDraft()) })
    }

    fun save() {
        val state = _uiState.value
        if (state.isSaving) return

        val meaningful = state.items.filterNot { it.isBlank }
        when {
            state.receiver.isBlank() ->
                return _uiState.update { it.copy(error = Localization.strings.errorEnterReceiver) }
            meaningful.isEmpty() ->
                return _uiState.update { it.copy(error = Localization.strings.errorAtLeastOneItem) }
            meaningful.any { !it.isValid } ->
                return _uiState.update {
                    it.copy(error = Localization.strings.errorIncompleteRow)
                }
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true, error = null) }
            val items = meaningful.map {
                DebtItem(
                    name = it.name.trim(),
                    quantity = it.quantity!!,
                    unit = it.unit,
                    unitPrice = it.unitPrice!!,
                )
            }
            debtRepository.upsertEntry(
                DebtEntry(
                    id = editingId ?: newId(),
                    supplierId = supplierId,
                    type = DebtEntryType.PURCHASE,
                    dateEpochMillis = state.dateEpochMillis,
                    description = state.receiver.trim(),
                    items = items,
                    amount = items.sumOf { it.total },
                )
            )
            _uiState.update { it.copy(isSaving = false, closed = true) }
        }
    }

    fun delete() {
        val id = editingId ?: return
        viewModelScope.launch {
            debtRepository.deleteEntry(id)
            _uiState.update { it.copy(closed = true) }
        }
    }

    /** Reset the one-shot close flag after navigation has been handled. */
    fun onClosedHandled() = _uiState.update { it.copy(closed = false) }
}

private fun formatDecimalForInput(value: Double): String =
    if (value == value.roundToLong().toDouble()) {
        value.roundToLong().toString()
    } else {
        value.toString().replace('.', ',')
    }
